import { useEffect, useRef } from 'react'
import {
  legacyMonthlyContractOptions,
  legacyMonthlyDataOptions,
  legacyMonthlyShootStatusOptions,
  legacyMonthlyTmCategoryOptions,
  legacyMonthlyTmStatusOptions,
  type MonthlyFilterKey,
  type MonthlyFilterOption,
} from '../../mock/legacyMonthlySchedule'
import './LegacyMonthlyFilter.css'

export type MonthlyFilters = {
  shootStatus: string[]
  contractType: string[]
  data: string[]
  tmCategory: string[]
  tmStatus: string[]
}

export const defaultMonthlyFilters: MonthlyFilters = {
  shootStatus: legacyMonthlyShootStatusOptions.slice(1).map((item) => item.id),
  contractType: legacyMonthlyContractOptions.slice(1).map((item) => item.id),
  data: ['photo', 'normal', 'work', 'tm', 'memo'],
  tmCategory: legacyMonthlyTmCategoryOptions.map((item) => item.id),
  tmStatus: legacyMonthlyTmStatusOptions.map((item) => item.id),
}

type LegacyMonthlyFilterProps = {
  open: boolean
  filters: MonthlyFilters
  onOpen: () => void
  onClose: () => void
  onApply: () => void
  onToggle: (key: MonthlyFilterKey, id: string) => void
}

function checkedAll(filters: MonthlyFilters, key: MonthlyFilterKey, options: MonthlyFilterOption[]) {
  if (key === 'data') {
    const required = ['photo', 'normal', 'work', 'tm', 'due', 'birthday', 'party', 'memo']
    return required.every((id) => filters.data.includes(id))
      && legacyMonthlyTmCategoryOptions.every((item) => filters.tmCategory.includes(item.id))
      && legacyMonthlyTmStatusOptions.every((item) => filters.tmStatus.includes(item.id))
  }
  return options.filter((item) => item.id !== 'all' && !item.disabled).every((item) => filters[key].includes(item.id))
}

function FilterOption({ option, filterKey, filters, onToggle, nested = false, idPrefix }: {
  option: MonthlyFilterOption
  filterKey: MonthlyFilterKey
  filters: MonthlyFilters
  onToggle: (key: MonthlyFilterKey, id: string) => void
  nested?: boolean
  idPrefix: string
}) {
  const isAll = option.id === 'all'
    || (filterKey === 'tmCategory' && option.id === 'tm_category_all')
    || (filterKey === 'tmStatus' && option.id === 'tm_status_all')
  const groupOptions = filterKey === 'shootStatus'
    ? legacyMonthlyShootStatusOptions
    : filterKey === 'contractType'
      ? legacyMonthlyContractOptions
      : filterKey === 'tmCategory'
        ? legacyMonthlyTmCategoryOptions
        : filterKey === 'tmStatus'
          ? legacyMonthlyTmStatusOptions
          : legacyMonthlyDataOptions
  const checked = isAll
    ? checkedAll(filters, filterKey, groupOptions)
    : filters[filterKey].includes(option.id)
  const dataInputIds: Record<string, string> = {
    all: 'data_all', photo: 'photo_yn', normal: 'normal_yn', work: 'work_yn', tm: 'data_tm',
    due: 'baby_duedate_yn', birthday: 'baby_birth_yn', party: 'baby_birthparty_day_yn', memo: 'memo_yn', trip: 'business_trip_yn',
  }
  const inputId = filterKey === 'data'
    ? dataInputIds[option.id]
    : filterKey === 'tmCategory'
      ? (option.id === 'tm_category_all' ? 'tm_sched2_all' : `tm_sched2_${option.id}`)
      : filterKey === 'tmStatus'
        ? (option.id === 'tm_status_all' ? 'tm_sched3_all' : option.id === '0,1' ? 'tm_sched3_1' : `tm_sched3_${option.id}`)
        : isAll ? `${idPrefix}-all` : `${idPrefix}-${option.id}`
  return <div className={nested ? 'filter_item2 s02-filter-item2' : 'filter_item s02-filter-item'}>
    <input
      id={inputId}
      className={nested ? `l_child_chk${option.id.endsWith('_all') ? ' data_tm' : ''}` : ''}
      name={filterKey === 'shootStatus' ? 'shoot_status' : filterKey === 'contractType' ? 'contract_type' : filterKey === 'tmCategory' ? 'tm_category' : filterKey === 'tmStatus' ? 'tm_status' : 'data'}
      type="checkbox"
      value={option.queryValue ?? option.id}
      checked={checked}
      disabled={option.disabled}
      onChange={() => onToggle(filterKey, isAll ? 'all' : option.id)}
    />
    <label htmlFor={inputId}>{option.label}</label>
  </div>
}

export function LegacyMonthlyFilter({ open, filters, onOpen, onClose, onApply, onToggle }: LegacyMonthlyFilterProps) {
  const holderRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const closeOutside = (event: MouseEvent) => {
      if (!holderRef.current?.contains(event.target as Node)) onClose()
    }
    document.addEventListener('mousedown', closeOutside)
    return () => document.removeEventListener('mousedown', closeOutside)
  }, [open, onClose])

  return <div className="s02-filter-holder" ref={holderRef}>
    <button id="filter_open" className="g_btn add size_2" type="button" disabled={open} onClick={onOpen}>표시선택</button>
    {open && <>{' '}<button id="l_filter_submit" className="g_btn add size_2" type="button" onClick={onApply}>적용</button></>}
    {open && <div id="filter_open_contents" className="filter_open_contents s02-filter-popover">
      <button id="l_close_filter_btn" className="g_btn icon close" type="button" aria-label="표시 선택 닫기" onClick={onClose} />
      <div className="filter_content_wrap s02-filter-content-wrap">
        <section id="shoot_status-dom" className="filter_item_wrap s02-filter-item-wrap g_scroll_bar seleted_status_wrap">
          <div className="label_title">촬영 스케줄 상태</div>
          {legacyMonthlyShootStatusOptions.map((option) => <FilterOption key={option.id} option={option} filterKey="shootStatus" filters={filters} onToggle={onToggle} idPrefix="shoot_status" />)}
        </section>
        <section id="contract_type-dom" className="filter_item_wrap s02-filter-item-wrap g_scroll_bar seleted_status_wrap">
          <div className="label_title">계약 구분</div>
          {legacyMonthlyContractOptions.map((option) => <FilterOption key={option.id} option={option} filterKey="contractType" filters={filters} onToggle={onToggle} idPrefix="contract_type" />)}
        </section>
        <section id="data-dom" className="filter_item_wrap s02-filter-item-wrap g_scroll_bar">
          <div className="label_title">표시할 데이터</div>
          {legacyMonthlyDataOptions.slice(0, 5).map((option) => <FilterOption key={option.id} option={option} filterKey="data" filters={filters} onToggle={onToggle} idPrefix="data" />)}
          {legacyMonthlyTmCategoryOptions.map((option, index) => <FilterOption key={option.id} option={option} filterKey="tmCategory" filters={filters} onToggle={onToggle} nested idPrefix={index === 0 ? 'tm_sched2' : 'tm_sched2'} />)}
          <hr />
          {legacyMonthlyTmStatusOptions.map((option) => <FilterOption key={option.id} option={option} filterKey="tmStatus" filters={filters} onToggle={onToggle} nested idPrefix="tm_sched3" />)}
          {legacyMonthlyDataOptions.slice(5, 9).map((option) => <FilterOption key={option.id} option={option} filterKey="data" filters={filters} onToggle={onToggle} idPrefix="data" />)}
          <hr />
          <div title="체크 시 출장촬영만 표시됩니다">
            <FilterOption option={legacyMonthlyDataOptions[9]} filterKey="data" filters={filters} onToggle={onToggle} idPrefix="data" />
          </div>
        </section>
      </div>
    </div>}
  </div>
}
